import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import Navbar from "./components/layout/Navbar";
import Header from "./components/layout/Header";

// Redux
import { Provider, useSelector } from "react-redux";
import store from "./store";
import { loadUser } from "./actions/auth";

import "./App.css";
import Dashboard from "./components/parenPortal/dashboard";
import Footer from "./components/layout/footer";
import CheckoutSummary from "./components/parenPortal/checkoutSummary/CheckoutSummary";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import ProfilePage from "./components/parenPortal/ProfilePage";
import OrderHistory from "./components/parenPortal/orders/OrderHistory";
import ProductManagement from "./components/admin/ProductManagement";
import ParentLogin from "./components/auth/ParentLogin";
import StudentManagement from "./components/admin/StudentManagement";
import OrderManagement from "./components/admin/OrderManagement";
import ThankYouPage from "./components/parenPortal/checkoutSummary/ThankyouPage";
import BundleManagement from "./components/admin/BundleManagement";
import Instructions from "./components/parenPortal/Instructions";
import BundleDetailsPage from "./components/parenPortal/products/BundleDetailsPage";
import PrivateRoute from "./components/routing/PrivateRoute";
import { ROLES } from "./utils/constants";
// import AuthRedirect from "./components/layout/AuthRedirect";
import FullPageSpinner from "./components/layout/FullPageSpinner";
// import SupportQueries from "./components/admin/SupportQueries";
import AdminLogin from "./components/auth/AdminLogin";
import AdminManagement from "./components/admin/AdminManagement";
import PasswordReset from "./components/auth/PasswordReset";
import Settings from "./components/admin/Settings";
import BulkSizeUpdate from "./components/admin/BulkSizeUpdate";
import Reports from "./components/admin/Reports";
import CartManagement from "./components/admin/CartManagement";
import SizeWeightManagement from "./components/admin/SizeWeightManagement";
import DtdcShipmentOrders from "./components/admin/DtdcShipmentOrders";

// Import new pages
import AboutUs from "./pages/about";
import ContactUs from "./pages/contact";
import TermsAndConditions from "./pages/terms";
import PrivacyPolicy from "./pages/privacy";
import RefundPolicy from "./pages/refund-policy";
import HomePage from "./pages/home";

import { ToastContainer } from "react-toastify";
// import MaintenancePage from "./pages/MaintenancePage";
const ProductListing = lazy(() =>
  import("./components/parenPortal/products/productList")
);
const ChildrenDetails = lazy(() =>
  import("./components/parenPortal/children/ChildrenDetails")
);

const Layout = ({ children }) => {
  const { isAuthenticated, loading, isFirstTimeLogin } = useSelector((state) => state.auth);
  const showNavAndFooter = !isFirstTimeLogin && isAuthenticated && !loading;

  return (
    <>
      {loading && <FullPageSpinner loading={loading} />}
      {/* Show Navbar only if authenticated, not loading, and not on first time login */}
      {showNavAndFooter ? <Navbar /> : <Header/>}
      {/* {children} */}
      <div style={{ paddingTop: "70px" }}>
        {children}
      </div>
      {/* {showNavAndFooter && <Footer />} */}
      <Footer />
    </>
  );
};

const DashboardRedirect = () => {
  const { user } = useSelector((state) => state.auth);
  const isParent = user?.role === ROLES.PARENT;

  return isParent ? <ChildrenDetails /> : <Dashboard />;
};

const App = () => {
  useEffect(() => {
    store.dispatch(loadUser());
  }, []);

  return (
    <Provider store={store}>
      <ToastContainer />
      <Router>
        <Suspense fallback={<FullPageSpinner loading={true} />}>
          <Layout>
            <Routes>
              
              <Route path="/" element={<HomePage />} />
              {/* <Route path="/" element={<AuthRedirect />} /> */}
              {/* <Route path="/login" element={<MaintenancePage />} /> */}
              <Route path="/login" element={<ParentLogin />} />
              <Route path="/parent/login" element={<ParentLogin />} />
              <Route
                path="/admin/login"
                element={<AdminLogin userType={"Admin"} />}
              />
              <Route
                path="/vendor/login"
                element={<AdminLogin userType={"Vendor"} />}
              />

              {/* Password Reset Route */}
              <Route element={<PrivateRoute />}>
                <Route path="/reset-password" element={<PasswordReset />} />
              </Route>

              {/* Public Routes */}
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />

              {/* Private Routes for Authenticated Users */}
              <Route element={<PrivateRoute />}>
                <Route path="/dashboard" element={<DashboardRedirect />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Route>

            <Route element={<PrivateRoute allowedRoles={[ROLES.PARENT]} />}>
              <Route exact path="/products" element={<ProductListing isBundle={false} />} />
              <Route exact path="/bundles" element={<ProductListing isBundle={true} />} />
              <Route path="/children" element={<ChildrenDetails />} />
              <Route path="/order/history" element={<OrderHistory />} />
              <Route path="/thankyou/:orderId" element={<ThankYouPage />} />
              <Route path="/instructions" element={<Instructions />} />
              <Route path="/cart" element={<CheckoutSummary />} />
              <Route path="/checkout" element={<CheckoutSummary />} />
              <Route path="/bundle/:bundleId/:studentId" element={<BundleDetailsPage />} />
            </Route>

            <Route element={<PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.ACCOUNTS, ROLES.STORES]} />}>
              <Route path="/admin/products" element={<ProductManagement />} />
              <Route path="/admin/students" element={<StudentManagement />} />
              <Route path="/admin/orders" element={<OrderManagement />} />
              <Route path="/admin/bundle" element={<BundleManagement />} />
              <Route path="/admin/manage" element={<AdminManagement />} />
              <Route path="/admin/bulk-size-update" element={<BulkSizeUpdate />} />
              <Route path="/admin/size-weights" element={<SizeWeightManagement />} />
              <Route path="/admin/reports" element={<Reports />} />
              <Route path="/admin/cart-management" element={<CartManagement />} />
              <Route element={<PrivateRoute allowedRoles={[ROLES.SUPER_ADMIN]} />}>
                <Route path="/admin/settings" element={<Settings />} />
              </Route>
            </Route>

            <Route element={<PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.ACCOUNTS, ROLES.STORES, ROLES.VENDOR]} />}>
              <Route path="/admin/dtdc-shipments" element={<DtdcShipmentOrders />} />
              <Route path="/vendor/dtdc-shipments" element={<DtdcShipmentOrders />} />
            </Route>

            <Route element={<PrivateRoute allowedRoles={[ROLES.VENDOR]} />}>
              <Route path="/vendor/orders" element={<OrderManagement isVendor={true} />} />
            </Route>
            <Route element={<PrivateRoute allowedRoles={[ROLES.STORES]} />}>
              <Route path="/stores/orders" element={<OrderManagement isVendor={false} isStore={true} />} />
            </Route>
            <Route element={<PrivateRoute allowedRoles={[ROLES.ACCOUNTS]} />}>
              <Route path="/accounts/orders" element={<OrderManagement isVendor={false} isStore={true} />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        </Suspense>
      </Router>
    </Provider>
  );
};

export default App;
